// src/components/common/Layout.jsx

import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import RoleSwitcher from '../RoleSwitcher';
import PayrollBanner from './PayrollBanner';
import TeamMessageModal from './TeamMessageModal';
import AuthSidebar from '../auth/AuthSidebar';
import {
  FiHome,
  FiCalendar,
  FiUsers,
  FiGrid,
  FiClock,
  FiShield,
  FiMenu,
  FiX,
  FiChevronDown,
  FiUser,
  FiLogOut,
  FiHelpCircle
} from 'react-icons/fi';

export default function Layout() {
  const { user } = useUser();
  const { currentUser, userRole, userProfile, hasPermission: authHasPermission, isFirebaseAvailable } = useAuth();
  const { currentTenant, availableTenants, switchTenant, hasPermission, loading: tenantLoading } = useTenant();

  const location = useLocation();
  const [settings, setSettings] = useState({ 
    logo: "", 
    clinicName: "", 
    websiteUrl: "", 
    themeColor: "#0ea5e9" 
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Load tenant-scoped settings
  useEffect(() => {
    async function loadTenantSettings() {
      if (!currentTenant?.id) {
        // Reset to default when no tenant
        setSettings({ 
          logo: "", 
          clinicName: "HealeHub", 
          websiteUrl: "", 
          themeColor: "#0ea5e9" 
        });
        return;
      }

      try {
        tenantDataService.setTenantContext(currentTenant.id);
        const tenantSettings = await tenantDataService.getById('settings', 'clinic_settings');
        if (tenantSettings) {
          setSettings({
            logo: tenantSettings.logo || "",
            clinicName: tenantSettings.clinicName || currentTenant.name || "HealeHub",
            websiteUrl: tenantSettings.websiteUrl || "",
            themeColor: tenantSettings.themeColor || "#0ea5e9"
          });
        } else {
          // Use tenant name as fallback
          setSettings(prev => ({
            ...prev,
            clinicName: currentTenant.name || "HealeHub"
          }));
        }
      } catch (error) {
        console.error('Error loading tenant settings:', error);
        // Use tenant name as fallback
        setSettings(prev => ({
          ...prev,
          clinicName: currentTenant.name || "HealeHub"
        }));
      }
    }

    loadTenantSettings();
    
    // Listen for settings updates
    const handleSettingsUpdate = () => loadTenantSettings();
    window.addEventListener("clinicflow-settings-updated", handleSettingsUpdate);
    
    return () => window.removeEventListener("clinicflow-settings-updated", handleSettingsUpdate);
  }, [currentTenant]);

  const navigationItems = [
    { path: '/', label: 'Dashboard', icon: FiHome },
    { path: '/calendar', label: 'Calendar', icon: FiCalendar },
    { path: '/clients', label: 'Clients', icon: FiUsers },
    { path: '/rooms', label: 'Rooms', icon: FiGrid },
    { path: '/waitlist', label: 'Waitlist', icon: FiClock },
    ...(hasPermission('admin') ? [
      { path: '/admin', label: 'Admin', icon: FiShield, adminOnly: true }
    ] : []),
    ...(userProfile?.role?.toLowerCase() === 'super_admin' ? [
      { path: '/super-admin', label: 'Super Admin', icon: FiShield, superAdminOnly: true }
    ] : [])
  ];

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };



  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Modern Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-20' : 'w-64'} transition-all duration-300 ease-in-out bg-white border-r border-gray-200 flex flex-col shadow-sm`}>
        {/* Logo Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center space-x-3">
                {settings.websiteUrl ? (
                  <a
                    href={settings.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                    title={`Visit ${settings.clinicName || 'clinic'} website`}
                  >
                    {settings.logo ? (
                      <img
                        src={settings.logo}
                        alt="Clinic logo"
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div 
                        className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-base"
                        style={{ backgroundColor: settings.themeColor || '#0ea5e9' }}
                      >
                        {(settings.clinicName || "CF").charAt(0)}
                      </div>
                    )}
                    <div>
                      <h1 className="text-lg font-bold text-gray-900">
                        {settings.clinicName || "HealeHub"}
                      </h1>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center space-x-3">
                    {settings.logo ? (
                      <img
                        src={settings.logo}
                        alt="Clinic logo"
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div 
                        className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-base"
                        style={{ backgroundColor: settings.themeColor || '#0ea5e9' }}
                      >
                        {(settings.clinicName || "CF").charAt(0)}
                      </div>
                    )}
                    <div>
                      <h1 className="text-lg font-bold text-gray-900">
                        {settings.clinicName || "HealeHub"}
                      </h1>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {sidebarCollapsed ? <FiMenu size={18} /> : <FiX size={18} />}
            </button>
          </div>
        </div>

        {/* Tenant Switcher - Only for Super Admins */}
        {!sidebarCollapsed && currentTenant && userProfile?.role?.toLowerCase() === 'super_admin' && (
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="text-xs font-medium text-gray-500 mb-2">Current Clinic</div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: currentTenant.branding?.primaryColor || '#2563eb' }}
                />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {currentTenant.name}
                </span>
              </div>
              {availableTenants.length > 1 && (
                <select
                  value={currentTenant.id}
                  onChange={(e) => switchTenant(e.target.value)}
                  className="text-xs border-0 bg-transparent focus:ring-0 p-0 cursor-pointer"
                >
                  {availableTenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2.5 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                title={sidebarCollapsed ? item.label : ''}
              >
                <Icon 
                  size={sidebarCollapsed ? 24 : 20} 
                  className={isActive ? 'text-blue-700' : 'text-gray-500'} 
                />
                {!sidebarCollapsed && (
                  <span className="font-medium">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Authentication Section - Bottom */}
        {!sidebarCollapsed && <AuthSidebar />}
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Header - Simplified for Development */}
        <header className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
            </div>
            
            {/* Role Switcher */}
            <div className="flex items-center space-x-3">
              {/* Role Switcher - Only show in development mode */}
              {!isFirebaseAvailable && <RoleSwitcher />}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 bg-gray-50 overflow-auto">
          <div className="fade-in">
            {/* Payroll Banner for Staff */}
            <PayrollBanner />
            
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* Team Message Modal */}
      <TeamMessageModal />
      
    </div>
  );
}